<script lang="ts">
import {RouteNames} from "@/utils/constants.ts";

export function getRouteOnboardPersonalProfile() {
  return {
    name: RouteNames.ONBOARDING_PERSON_PROFILE
  };
}
</script>

<script setup lang="ts">

import FormField from "@/components/form/FormField.vue";
import CardLayout from "@/components/layout/CardLayout.vue";
import BasicFormTitle from "@/components/basic/BasicFormTitle.vue";
import FormRequester from "@/core/libs/FormRequester/FormRequester.ts";
import PersonService from "@/services/personService.ts";
import PersonProfileFields from "@/services/PersonService/PersonProfileFields.ts";
import BasicLoader from "@/components/basic/BasicLoader.vue";
import { computed } from "vue";
import { useRouter, useRoute } from "vue-router";
import { getRouteXDashboard } from "@/views/Auth/Dashboard.vue";
import { usePersonStore } from "@/store";
import { getRouteOnboardPersonalQuestionnaire } from "@/views/Onboarding/OnboardingPersonalQuestionnaire.vue";
import { getRouteOnboardBusinessProfile } from "@/views/Onboarding/OnboardingBusinessProfile.vue";


const form = new FormRequester<PersonService, any>("personal-profile", new PersonService(), "createPersonalProfile", PersonProfileFields, null);
const {
  fieldsRefs: {
    firstName,
    lastName,
    dateOfBirth,
    personalCode,
    citizenship,
    taxCountry,
    taxNumber,
    addressLine,
    addressCity,
    addressCountry,
    addressPostcode,
    isPep,
    isUS
  },
  isLoading,
  isAllowContinue,
  formError
} = form;

const router = useRouter();
const route = useRoute();

const personStore = usePersonStore();

// Check if this is business onboarding context
const isBusiness = route.query.type === 'business';

// For business context with existing profile, allow continue without form validation
const isAllowedToContinue = computed(() => {
  if (isBusiness && personStore.person?.profile) {
    return true; // Allow continue if business context and profile exists
  }
  return isAllowContinue.value; // Otherwise use form validation
});

// For individual onboarding only: redirect to questionnaire if user already has a profile
// For business onboarding: always show the form (APPLICANT step)
if (personStore.person?.profile && !isBusiness) {
  try {
    router.push(getRouteOnboardPersonalQuestionnaire());
  } catch (e) {
    console.error(e);
  }
}

async function submitForm(): Promise<void> {
  // If business context and profile already exists, skip form submission
  if (isBusiness && personStore.person?.profile) {
    // Profile already exists, just navigate to Business Profile
    await router.push(getRouteOnboardBusinessProfile());
    return;
  }

  await form.send();

  if (formError.value === null) {
    // Refresh person state to get updated profile data
    await personStore.getState();

    // Navigate to next step based on context
    if (isBusiness) {
      // For business onboarding, go to Business Profile page
      await router.push(getRouteOnboardBusinessProfile());
    } else {
      // For individual onboarding, go to Questionnaire page
      await router.push(getRouteOnboardPersonalQuestionnaire());
    }
  }
}
</script>

<template>
  <CardLayout
    :title="isBusiness ? 'Business Account' : 'Personal info'"
    :subtitle="isBusiness ? 'Application for opening and maintaining a business current account' : 'Application for opening and maintaining a personal current account'"
    heading="Applicant"
    :max-steps="isBusiness ? 4 : 4"
    :current-step="1"
    :next="submitForm"
    :back="getRouteXDashboard()"
    :isAllowedNext="isAllowedToContinue"
  >
    <div class="flex flex-col w-full gap-8">
      <div class="px-4 md:px-0">
        <BasicFormTitle>
          Personal Details
        </BasicFormTitle>
      </div>


      <div class="text-[14px]/[38px] text-red pt-0 px-4 md:px-0" v-if="formError">
        <div class="flex gap-x-4 items-center">
          <svg width="23" height="23" viewBox="0 0 23 23" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M11.9635 6.30909H11.9272C11.4908 6.30909 11.0908 6.67273 11.0908 7.14545C11.0908 7.61818 11.4545 7.98182 11.9272 7.98182H11.9635C12.3999 7.98182 12.7999 7.61818 12.7999 7.14545C12.7999 6.67273 12.4363 6.30909 11.9635 6.30909Z" fill="#FF7070"/>
            <path d="M11.9633 9.18182C11.527 9.18182 11.127 9.54545 11.127 10.0182V15.6182C11.127 16.0545 11.4906 16.4545 11.9633 16.4545C12.3997 16.4545 12.7997 16.0909 12.7997 15.6182V9.98182C12.7997 9.54545 12.436 9.18182 11.9633 9.18182Z" fill="#FF7070"/>
            <path d="M11.9635 0.0181808C5.89077 0.0181808 0.945312 4.92727 0.945312 11C0.945312 17.0727 5.89077 22.0182 11.9635 22.0182C18.0362 22.0182 22.9453 17.0727 22.9453 11C22.9453 4.92727 18.0362 0.0181808 11.9635 0.0181808ZM11.9635 20.3818C6.79986 20.3818 2.58168 16.1636 2.58168 11C2.58168 5.83636 6.79986 1.61818 11.9635 1.61818C17.1271 1.61818 21.3089 5.83636 21.3089 11C21.3089 16.1636 17.1271 20.3818 11.9635 20.3818Z" fill="#FF7070"/>
          </svg>

          {{ formError }}
        </div>
      </div>

      <div class="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-8 px-4 md:px-0">
        <FormField type="text" :model="firstName" name="firstName" label="First Name" />
        <FormField type="text" :model="lastName" name="lastName" label="Last Name" />
        <FormField type="date" :model="dateOfBirth" name="dateOfBirth" label="Date of Birth" />
        <FormField type="text" :model="personalCode" name="personalCode" label="Personal Code" />
        <FormField type="country" :model="citizenship" name="citizenship" label="Citizenship" />
        <div class="flex flex-col gap-y-3">
          <div class="text-sm font-normal text-gray-900">Politically Exposed Person</div>
          <FormField type="checkbox" :model="isPep" name="isPep" label="PEP or relatives PEP?">
            PEP or relatives PEP?
          </FormField>
          <FormField type="checkbox" :model="isUS" name="isUS" label="A citizen or tax resident of US">
            A citizen or tax resident of US
          </FormField>
        </div>
      </div>
    </div>

    <div class="flex flex-col w-full gap-8">
      <div class="px-4 md:px-0">
        <BasicFormTitle>
          Tax Information
        </BasicFormTitle>
      </div>
      <div class="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-8 px-4 md:px-0">
        <FormField type="country" :model="taxCountry" name="taxCountry" label="Tax Country" />
        <FormField type="text" :model="taxNumber" name="taxNumber" label="Tax Number" />
      </div>
    </div>

    <div class="flex flex-col w-full gap-8">
      <div class="px-4 md:px-0">
        <BasicFormTitle>
          Address Information
        </BasicFormTitle>
      </div>
      <div class="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-8 px-4 md:px-0">
        <FormField type="country" :model="addressCountry" name="addressCountry" label="Country" />
        <FormField type="text" :model="addressCity" name="addressCity" label="City" />
        <FormField type="text" :model="addressPostcode" name="addressPostcode" label="Postcode/Zip Code" />
        <FormField type="text" :model="addressLine" name="addressPostcode" label="Address Line" />
      </div>
    </div>
    <BasicLoader :show="isLoading" />
  </CardLayout>
</template>

<style scoped lang="scss">

</style>
